import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { submitAiFeedback } from '@/lib/feedback';
import useUser from '@/lib/hooks/use-user';
import { toast } from 'sonner';
import { MessageSquare, AlertCircle, ThumbsUp, HelpCircle } from 'lucide-react';
import { Analytics } from '@/lib/analytics';
import { logError } from '@/lib/error-logging';

interface FeedbackModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    context?: {
        passageId?: string;
        contextType?: string;
        modelInfo?: string;
        contentId?: string;
    };
}

export function FeedbackModal({ open, onOpenChange, context }: FeedbackModalProps) {
    const { user } = useUser();
    const [type, setType] = useState<FeedbackType>(context ? 'content_inaccuracy' : 'suggestion');
    const [content, setContent] = useState('');
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Update email when user changes or modal opens
    useEffect(() => {
        if (open && user?.email) {
            setEmail(user.email);
        }
    }, [open, user]);

    const handleSubmit = async () => {
        if (!content.trim()) return;

        if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            toast.error("Invalid email address", {
                description: "Please provide a valid email address or leave it blank.",
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const feedbackData: AIFeedbackData = {
                userId: user?.uid || 'anonymous',
                email: email.trim() || undefined,
                type,
                content: content.trim(),
                ...context,
                url: window.location.href,
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString(),
            };

            const result = await submitAiFeedback(feedbackData);

            if (result.success) {
                Analytics.feedbackSubmitted(type, !!context);
                toast.success("Feedback sent!", {
                    description: "Thank you for helping us improve Bible Glen.",
                });
                setContent('');
                onOpenChange(false);
            } else {
                toast.error("Error submitting feedback", {
                    description: result.error || "Please try again later.",
                });
            }
        } catch (error) {
            logError(error, {
                component: 'FeedbackModal',
                action: 'submit_feedback',
                additionalInfo: { type, hasContext: !!context }
            });
            console.error('Error submitting feedback:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const categories = [
        { value: 'content_inaccuracy', label: 'Inaccurate Content', icon: AlertCircle },
        { value: 'bug', label: 'Technical Bug', icon: AlertCircle },
        { value: 'suggestion', label: 'Feature Suggestion', icon: MessageSquare },
        { value: 'praise', label: 'Kudos', icon: ThumbsUp },
        { value: 'other', label: 'Other', icon: HelpCircle },
    ];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogTitle className="sr-only">Send Feedback</DialogTitle>
                <DialogDescription className="sr-only">
                    {context
                        ? "Report an issue or suggest an improvement for this specific content."
                        : "Have a question or suggestion? We'd love to hear from you."
                    }
                </DialogDescription>
                <DialogHeader>
                    <div className="flex items-center gap-2 text-lg font-semibold">
                        <MessageSquare className="h-5 w-5 text-primary" />
                        Send Feedback
                    </div>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label>What's the feedback about?</Label>
                        <Select value={type} onValueChange={(v) => setType(v as FeedbackType)}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select a category" />
                            </SelectTrigger>
                            <SelectContent>
                                {categories.map((cat) => (
                                    <SelectItem key={cat.value} value={cat.value}>
                                        <div className="flex items-center gap-2">
                                            <cat.icon className="h-4 w-4" />
                                            {cat.label}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="feedback-email">Email (Optional, for responses)</Label>
                        <Input
                            id="feedback-email"
                            type="email"
                            placeholder="your@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? "border-destructive" : ""}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="feedback-content">Details</Label>
                        <Textarea
                            id="feedback-content"
                            placeholder={type === 'content_inaccuracy' ? "Describe why this information is inaccurate..." : "Tell us more..."}
                            className="min-h-[120px]"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                        />
                    </div>

                    {context && (
                        <div className="p-3 bg-muted rounded-md text-[10px] space-y-1">
                            <p className="font-semibold text-muted-foreground uppercase tracking-wider">Debug Info</p>
                            <div className="grid grid-cols-2 gap-x-4">
                                {context.passageId && <div>Passage: {context.passageId}</div>}
                                {context.contextType && <div>Section: {context.contextType}</div>}
                                {context.contentId && <div className="col-span-2">Content ID: {context.contentId}</div>}
                                {context.modelInfo && <div className="col-span-2">Model: {context.modelInfo}</div>}
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={isSubmitting || !content.trim()}>
                        {isSubmitting ? 'Sending...' : 'Submit Feedback'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
